'use client';

import type { Entry, UnresolvedLink } from 'contentful';
import { CtaAddOnVariant, CtaAddOnAlignment, type CtaAddOnRuntimeFields, type CtaAddOnRuntimeProps } from './types';
import type { CtaRuntimeEntry } from 'components/shared/CTA/types';
import type { LinkedIconSkeleton } from 'components/shared/LinkedIcon/types';
import { toCta } from 'components/shared/CTA/toCta';
import { toLinkedIcon } from 'components/shared/LinkedIcon/toLinkedIcon';
import KeyLine from '@/components/shared/KeyLine';
import RichTextHeadline from '@/components/shared/RichTextHeadline/RichTextHeadline';
import { toDocumentWithEmbeddedEntries } from 'libs/contentful/toDocumentWithEmbeddedEntries';
import { KeyLinePosition, Margins } from '@/types/constants';
import { cn, getColorwayWithTransparency, getSectionProps } from '@/utils/styles';
import { liveUpdateAttrs } from '@/libs/contentful/utils';
import CTA from 'components/shared/CTA/CTA';
import LinkedIcon from 'components/shared/LinkedIcon/LinkedIcon';
import { useEffect, useState } from 'react';
import { getEntryNativeFields } from 'utils/entryNativeFields';
import { getModuleSectionName } from 'utils/modules';

const getButtonOnlyYMargins = (margins: Margins) => {
  // Keyed by an enum so the annotation enforces exhaustiveness; dropping it would let a
  // new enum member go unhandled silently.
  // oxlint-disable-next-line anti-slop/no-known-value-widening
  const marginStyles: { [key in Margins]: string } = {
    [Margins.NoTop]: 'pt-0 pb-6',
    [Margins.NoBottom]: 'pt-6 pb-0',
    [Margins.NoMargins]: 'pt-1 pb-2', // Add tiny top and bottom margins to prevent button from being completely flush with background color
    [Margins.Centered]: 'py-6',
  };

  return marginStyles[margins] || marginStyles[Margins.NoMargins];
};

const getKeylinePadding = (margins: Margins, showTopKeyline: boolean): string => {
  if (margins == Margins.Centered) {
    if (showTopKeyline) {
      return 'pt-12';
    } else {
      return 'sm:pt-20 pt-12';
    }
  }
  return '';
};

type ResolvedLinkedIconEntry = Entry<LinkedIconSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>;

type CtaSource = CtaRuntimeEntry | ResolvedLinkedIconEntry | UnresolvedLink<'Entry'> | undefined;

const isLinkedIcon = (cta: CtaSource): cta is ResolvedLinkedIconEntry =>
  cta != null && 'fields' in cta && cta.sys.contentType.sys.id === 'linkedIcon';

const CtaRenderer = ({ cta, className }: { cta: CtaSource; className?: string }) => {
  if (isLinkedIcon(cta)) {
    const linkedIcon = toLinkedIcon(cta);
    return linkedIcon ? <LinkedIcon {...linkedIcon} /> : null;
  }

  const flatCta = toCta(cta);
  return flatCta ? <CTA cta={flatCta} className={className} /> : null;
};

const CtaAddOn = (props: CtaAddOnRuntimeProps) => {
  const { sys } = props;
  const { color, keyLine, margins, headline, cta, variant, alignment, transparentBackground, pinCtaAboveFold } =
    getEntryNativeFields<CtaAddOnRuntimeFields>(props);
  const sectionName = getModuleSectionName(props);

  const showTopKeyline = keyLine === KeyLinePosition.Top;
  const [bannerHeight, setBannerHeight] = useState(0);

  const headlineDoc = headline
    ? toDocumentWithEmbeddedEntries(headline, { parentTypename: 'CopyAddOnWithButton', fieldName: 'headline' })
    : undefined;

  // For CTAs pinned above the fold, we need the bottom position to
  // be adjusted based on the height of the disclosure banner (if present)
  useEffect(() => {
    if (!pinCtaAboveFold) return;

    const updateBannerHeight = () => {
      const banner = document.querySelector('[data-disclosure-banner]');
      setBannerHeight(banner?.clientHeight || 0);
    };

    updateBannerHeight();

    // Listen for banner dismissal event from DisclosureBanner
    const handleBannerDismissed = () => setBannerHeight(0);
    window.addEventListener('disclosureBannerDismissed', handleBannerDismissed);

    return () => window.removeEventListener('disclosureBannerDismissed', handleBannerDismissed);
  }, [pinCtaAboveFold]);

  const stickyBottomStyle = pinCtaAboveFold && bannerHeight > 0 ? { bottom: `${bannerHeight}px` } : undefined;

  if (variant === CtaAddOnVariant.ButtonOnly)
    return (
      <section
        {...getSectionProps(
          undefined,
          undefined,
          sectionName,
          getColorwayWithTransparency(color, transparentBackground),
        )}
        data-testid="copyAddOnWithButton"
        className={cn(
          pinCtaAboveFold &&
            'max-sm:sticky max-sm:bottom-0 max-sm:px-4 max-sm:z-24 max-sm:transition-[bottom] max-sm:duration-300',
        )}
        style={stickyBottomStyle}>
        <div className={cn('content-wrapper', getButtonOnlyYMargins(margins))}>
          <div
            className={cn(' flex min-w-full', {
              'justify-center': alignment === CtaAddOnAlignment.Centered,
              'justify-end': alignment === CtaAddOnAlignment.Right,
            })}>
            <CtaRenderer cta={cta} className={'sm:max-w-fit'} />
          </div>
        </div>
      </section>
    );

  return (
    <section {...getSectionProps(color, undefined, sectionName)} data-testid="copyAddOnWithButton">
      <div className="content-wrapper">
        {showTopKeyline && <KeyLine />}
        <div className={cn('sm:pb-20 pb-12', getKeylinePadding(margins, showTopKeyline))}>
          <div className="flex flex-col sm:flex-row justify-between gap-6">
            {headlineDoc && (
              <div
                className="basis-1/2 text-heading-lg font-sans font-semibold"
                {...liveUpdateAttrs(sys?.id, 'headline')}>
                <RichTextHeadline headline={headlineDoc} />
              </div>
            )}

            <div className="basis-1/2 flex flex-col sm:items-end">
              <CtaRenderer cta={cta} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CtaAddOn;
